import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Loader, Users, Zap, Building2, Lock, Bell, Star, ArrowRight, Flame } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

// ─── Stripe checkout helper ─────────────────────────────────────────────────
async function startCheckout(packageId, setLoading) {
  setLoading(packageId);
  try {
    const res = await fetch(`${API}/api/payments/stripe/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
    });
    const data = await res.json();
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
    } else {
      alert(data.detail || 'Something went wrong. Please try again.');
    }
  } catch {
    alert('Network error. Please try again.');
  } finally {
    setLoading(null);
  }
}

// ─── Sub-components ─────────────────────────────────────────────────────────
const Logo = ({ dark = false }) => (
  <Link to="/" className="flex items-center gap-0">
    <span
      className="text-2xl font-black tracking-tight"
      style={{ color: dark ? '#fff' : '#094f6f' }}
    >Rental</span>
    <img
      src="/rentalx-x-new.svg"
      alt="X"
      style={{ height: '1.6rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.02em', verticalAlign: 'middle' }}
    />
  </Link>
);

const CheckItem = ({ text, light = false }) => (
  <div className="flex items-start gap-3">
    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: BRAND }}>
      <Check className="w-3 h-3 text-white" />
    </div>
    <span className={`text-sm leading-relaxed ${light ? 'text-gray-100' : 'text-gray-600'}`}>{text}</span>
  </div>
);

const PLANS = [
  {
    id: 'founding_student',
    icon: Users,
    label: 'Students',
    price: '4.99',
    tagline: 'Find verified rooms with VibeMate lifestyle matching',
    cta: 'Join as Student',
    highlight: false,
    badge: null,
    features: [
      'VibeMate housemate matching',
      'Browse verified listings early',
      'Private founding member community',
      'Priority access at launch',
      'Founding price locked forever',
    ],
  },
  {
    id: 'founding_professional',
    icon: Zap,
    label: 'Working Professionals',
    price: '9.99',
    tagline: 'Skip the scroll. Find your perfect rental match.',
    cta: 'Join as Professional',
    highlight: true,
    badge: 'Most Popular',
    features: [
      'AI-powered rental matching',
      'Skip student queues for listings',
      'Private founding member community',
      'Early access to premium features',
      'Founding price locked forever',
    ],
  },
  {
    id: 'founding_landlord',
    icon: Building2,
    label: 'Property Managers',
    price: '19.99',
    tagline: 'Get quality tenants before listings go public',
    cta: 'List My Property',
    highlight: false,
    badge: null,
    features: [
      'List before public launch',
      'Pre-vetted tenant leads',
      'Admin dashboard access',
      'VibeMate tenant matching',
      'Founding price locked forever',
    ],
  },
];

const BENEFITS = [
  { icon: Users,  title: 'Private Community',   desc: 'Join an exclusive Slack/Discord with other founding members and the RentalX team.' },
  { icon: Bell,   title: 'Weekly Build Updates', desc: 'Get behind-the-scenes progress reports — see exactly what we\'re building each week.' },
  { icon: Star,   title: 'Early Feature Voting', desc: 'Vote on which features we build next. Your feedback shapes the product.' },
  { icon: Lock,   title: 'First Pick at Launch', desc: 'Get first access to new listings the moment we go live — before the general public.' },
];

const FAQS = [
  {
    q: 'Why pay before you\'ve launched?',
    a: 'Paying secures your founding member price — permanently. Once we launch publicly, prices increase. Your subscription locks in the founding rate forever, even as we add more features. If you\'re not satisfied within 30 days, we\'ll refund you, no questions asked.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel your subscription at any time from your account settings or by emailing us at hello@contact.rentalx.io. No lock-in periods, no cancellation fees. If you cancel and want to rejoin later, you\'ll pay the public price.',
  },
  {
    q: 'When do you launch?',
    a: 'We\'re 6–8 weeks from our full public launch. Founding members get access to the platform in a private beta starting in 2–3 weeks. You\'ll be testing real features and giving us feedback before anyone else.',
  },
  {
    q: 'What\'s the difference between the plans?',
    a: 'Students get VibeMate housemate matching and verified room listings. Working Professionals get AI-matched rentals tailored for professionals without the student competition. Property Managers get early access to list properties and receive pre-vetted tenant leads before we open to the public.',
  },
];

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function FoundingWaitlist() {
  const [count, setCount]       = useState(null);
  const [loading, setLoading]   = useState(null);   // package_id being loaded
  const [email, setEmail]       = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [openFaq, setOpenFaq]   = useState(null);

  // ── Live counter ────────────────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/waitlist-count`);
      const d = await r.json();
      if (typeof d.count === 'number') setCount(d.count);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // ── Free waitlist submit ─────────────────────────────────────────────────────
  const handleFreeSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setEmailStatus('loading');
    try {
      const r = await fetch(`${API}/api/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (d.status === 'error') { setEmailStatus('error'); return; }
      setEmailStatus('success');
    } catch {
      setEmailStatus('error');
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(160deg, #041e2b 0%, #083a54 50%, #094f6f 100%)' }} className="relative overflow-hidden">
        {/* subtle grain overlay */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }} />

        {/* Nav */}
        <div className="relative max-w-6xl mx-auto px-5 pt-6 pb-2 flex justify-between items-center">
          <Logo dark />
          <Link to="/pricing" className="text-sm font-semibold text-white/60 hover:text-white transition-colors">
            Full Pricing →
          </Link>
        </div>

        {/* Hero content */}
        <div className="relative max-w-4xl mx-auto px-5 pt-14 pb-20 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white rounded-full px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-[#2cbdc4] animate-pulse" />
            Pre-Launch — Founding Member Pricing
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6">
            Join RentalX Before<br className="hidden sm:block" />
            We Launch —{' '}
            <span style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundImage: 'linear-gradient(90deg,#2cbdc4,#7dd8dc)' }}>
              Founding Member
            </span>
            <br className="hidden sm:block" />
            Pricing, Locked In Forever
          </h1>

          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            We're 6–8 weeks from launch. Pay your first month now and lock in founding
            pricing that <strong className="text-white/90">never goes up.</strong>
          </p>

          {/* Live counter */}
          <div className="inline-flex items-center gap-2.5 bg-white/10 border border-white/20 rounded-2xl px-5 py-3 backdrop-blur-sm mb-10">
            <Flame className="w-5 h-5 text-orange-400" />
            {count === null ? (
              <span className="text-white/70 text-sm font-medium">Loading counter…</span>
            ) : (
              <span className="text-white font-bold text-sm" data-testid="founding-counter">
                {count === 0
                  ? 'Be the first founding member — join now'
                  : `${count} founding member${count === 1 ? '' : 's'} have already joined`}
              </span>
            )}
          </div>

          {/* Scroll cue */}
          <div className="flex flex-col items-center gap-1 text-white/40 text-xs mt-2">
            <span>Choose your plan below</span>
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </div>
        </div>
      </section>

      {/* ── PRICING CARDS ────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#2cbdc4] mb-2">Founding Prices</p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">Choose your plan</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Pay your first month now. This price is yours forever — even when public prices go up at launch.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const isLoading = loading === plan.id;
              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`relative bg-white rounded-3xl p-8 flex flex-col transition-all duration-200 ${
                    plan.highlight
                      ? 'ring-2 ring-[#2cbdc4] shadow-2xl scale-105'
                      : 'border border-gray-200 shadow-md hover:shadow-xl hover:-translate-y-1'
                  }`}
                >
                  {plan.badge && (
                    <div
                      className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap"
                      style={{ background: BRAND }}
                    >{plan.badge}</div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: plan.highlight ? BRAND : '#f0f7ff' }}>
                      <Icon className={`w-5 h-5 ${plan.highlight ? 'text-white' : 'text-[#094f6f]'}`} />
                    </div>
                    <h3 className="text-lg font-black text-gray-900">{plan.label}</h3>
                  </div>

                  <p className="text-sm text-gray-500 mb-5 leading-relaxed">{plan.tagline}</p>

                  <div className="mb-6">
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-black text-gray-900">A${plan.price}</span>
                      <span className="text-gray-400 text-sm mb-1.5">/month</span>
                    </div>
                    <p className="text-xs text-green-600 font-semibold mt-1">Founding price — locked forever</p>
                  </div>

                  <div className="space-y-2.5 flex-1 mb-7">
                    {plan.features.map((f, i) => <CheckItem key={i} text={f} />)}
                  </div>

                  <button
                    data-testid={`cta-${plan.id}`}
                    onClick={() => startCheckout(plan.id, setLoading)}
                    disabled={!!loading}
                    className="w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2 text-white hover:opacity-90 hover:scale-[1.02] active:scale-100"
                    style={{ background: BRAND }}
                  >
                    {isLoading ? (
                      <><Loader className="w-4 h-4 animate-spin" /> Redirecting…</>
                    ) : (
                      <>{plan.cta} <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            All prices in AUD incl. GST · Billed monthly via Stripe · Cancel anytime
          </p>
        </div>
      </section>

      {/* ── WHAT YOU GET ─────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#2cbdc4] mb-2">Immediate Access</p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-3">What you get right now</h2>
            <p className="text-gray-500 text-sm">The moment your payment goes through, you're in.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="flex gap-4 p-6 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-white hover:shadow-md transition-all">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BRAND }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FREE EMAIL CAPTURE ───────────────────────────────────────────────── */}
      <section style={{ background: 'linear-gradient(160deg, #041e2b 0%, #094f6f 100%)' }} className="py-16 px-5">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-[#2cbdc4] mb-3">Not ready to pay yet?</p>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">Get notified when we launch</h2>
          <p className="text-white/60 text-sm mb-8 leading-relaxed">
            Drop your email and we'll send you a heads-up the moment RentalX goes live.
            No spam — just the one important email.
          </p>

          {emailStatus === 'success' ? (
            <div className="bg-white/10 border border-white/20 rounded-2xl px-6 py-5 inline-flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-white" />
              </div>
              <p className="text-white font-semibold text-sm">You're on the list! We'll email you at launch.</p>
            </div>
          ) : (
            <form onSubmit={handleFreeSignup} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                data-testid="waitlist-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/40 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#2cbdc4] focus:bg-white/15 transition-all"
              />
              <button
                data-testid="waitlist-notify-btn"
                type="submit"
                disabled={emailStatus === 'loading'}
                className="flex-shrink-0 flex items-center justify-center gap-2 text-white font-bold text-sm px-6 py-3 rounded-xl transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND }}
              >
                {emailStatus === 'loading' ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <>Notify me at launch <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
          )}
          {emailStatus === 'error' && (
            <p className="text-red-400 text-xs mt-3">Something went wrong. Please try again.</p>
          )}
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 px-5">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-[#2cbdc4] mb-2">Got Questions?</p>
            <h2 className="text-3xl font-black text-gray-900">Frequently asked</h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-4 h-4 text-[#2cbdc4] flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 px-5 border-t border-gray-200">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs font-bold text-gray-600 mb-6 shadow-sm">
            <Flame className="w-3.5 h-3.5 text-orange-400" />
            {count !== null && count > 0
              ? `${count} founding member${count === 1 ? '' : 's'} already in`
              : 'Limited founding spots'}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4 leading-tight">
            Lock in your founding price<br className="hidden sm:block" /> before it's gone
          </h2>
          <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto leading-relaxed">
            Once we launch publicly, prices increase. This is a one-time opportunity
            to lock in founding pricing permanently.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {PLANS.map((plan) => (
              <button
                key={plan.id}
                data-testid={`cta-bottom-${plan.id}`}
                onClick={() => startCheckout(plan.id, setLoading)}
                disabled={!!loading}
                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-1.5 ${
                  plan.highlight
                    ? 'text-white hover:opacity-90'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-[#094f6f] hover:text-[#094f6f]'
                }`}
                style={plan.highlight ? { background: BRAND } : {}}
              >
                {loading === plan.id ? <Loader className="w-4 h-4 animate-spin" /> : null}
                A${plan.price} — {plan.cta}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-white py-8 px-5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
          <Logo dark />
          <div className="text-gray-500 text-xs text-center">
            © {new Date().getFullYear()} RentalX · Australia's Student Accommodation Marketplace
          </div>
          <div className="flex gap-4 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <a href="mailto:hello@contact.rentalx.io" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
